import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { dashboardAPI, api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DollarSign, ShoppingBag, Users, Package, AlertTriangle, TrendingUp, Crown, Clock, AlertCircle, CreditCard, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [showLicenseAlert, setShowLicenseAlert] = useState(true);
  const [paymentConfigured, setPaymentConfigured] = useState(true); // assume OK até saber
  const [showPaymentAlert, setShowPaymentAlert] = useState(true);

  useEffect(() => {
    loadStats();
    // Super admin (dono do SaaS) não tem assinatura — não carrega nada relacionado.
    if (user?.role !== 'super_admin') {
      loadSubscription();
    } else {
      // Super admin: verificar se o gateway de pagamento (Mercado Pago) está configurado
      // — sem isso, os CLIENTES não conseguem pagar mensalidade.
      api
        .get('/payment/config')
        .then((res) => setPaymentConfigured(!!res.data?.configured))
        .catch(() => setPaymentConfigured(true)); // silenciar erros
    }
  }, [user?.role]);

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await api.get('/saas/subscription');
      setSubscription(response.data);
    } catch (error) {
      console.log('Erro ao carregar assinatura');
    }
  };

  // Calcular dias restantes da licença
  const getDaysRemaining = () => {
    if (!subscription?.tenant?.subscription_end) return null;
    const endDate = new Date(subscription.tenant.subscription_end);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining();
  const planName = subscription?.plan?.name || 'Não definido';
  const isSuperAdmin = user?.role === 'super_admin';
  const isExpired = !isSuperAdmin && daysRemaining !== null && daysRemaining <= 0;
  const isUrgent = !isSuperAdmin && daysRemaining !== null && daysRemaining <= 3 && daysRemaining > 0;
  const isWarning = !isSuperAdmin && daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 3;
  // Super admin NUNCA vê alerta de licença — ele é o dono da plataforma.
  const showAlert = !isSuperAdmin && showLicenseAlert && daysRemaining !== null && daysRemaining <= 15;

  if (loading) {
    return <div className="p-8">Carregando...</div>;
  }

  const cards = [
    {
      title: 'Vendas Hoje',
      value: formatCurrency(stats?.today_sales || 0),
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
    {
      title: 'Transações Hoje',
      value: stats?.today_transactions || 0,
      icon: ShoppingBag,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Total Clientes',
      value: stats?.total_customers || 0,
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Produtos Ativos',
      value: stats?.total_products || 0,
      icon: Package,
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
    },
    {
      title: 'Estoque Baixo',
      value: stats?.low_stock_products || 0,
      icon: AlertTriangle,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
    },
    {
      title: 'Vendas do Mês',
      value: formatCurrency(stats?.this_month_sales || 0),
      icon: TrendingUp,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* 🆕 Alerta para super_admin: Mercado Pago NÃO configurado
          → clientes não conseguem pagar mensalidade */}
      {isSuperAdmin && !paymentConfigured && showPaymentAlert && (
        <div
          className="mb-6 rounded-lg border-2 border-amber-500 bg-amber-50 p-4 relative"
          data-testid="payment-not-configured-alert"
        >
          <button
            onClick={() => setShowPaymentAlert(false)}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-start gap-4 pr-6">
            <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-amber-900">
                🔔 Configure o Mercado Pago para receber assinaturas
              </h3>
              <p className="mt-1 text-amber-800">
                Sem isso, seus <strong>clientes não conseguem pagar</strong> as mensalidades
                (PIX ou cartão). Depois de configurado, a liberação do plano acontece
                automaticamente via webhook.
              </p>
              <div className="mt-3">
                <Button
                  onClick={() => navigate('/admin?tab=payments')}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="btn-configure-payments"
                >
                  Configurar agora
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerta de Renovação de Licença */}
      {showAlert && (
        <div className={`mb-6 rounded-lg border-2 p-4 relative ${
          isExpired ? 'bg-rose-50 border-rose-500' :
          isUrgent ? 'bg-amber-50 border-amber-500' :
          isWarning ? 'bg-yellow-50 border-yellow-400' :
          'bg-blue-50 border-blue-400'
        }`}>
          <button 
            onClick={() => setShowLicenseAlert(false)}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              isExpired ? 'bg-rose-500' :
              isUrgent ? 'bg-amber-500' :
              isWarning ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}>
              {isExpired ? (
                <AlertCircle className="w-6 h-6 text-white" />
              ) : isUrgent || isWarning ? (
                <AlertTriangle className="w-6 h-6 text-white" />
              ) : (
                <Clock className="w-6 h-6 text-white" />
              )}
            </div>
            
            <div className="flex-1">
              <h3 className={`font-bold text-lg ${
                isExpired ? 'text-rose-800' :
                isUrgent ? 'text-amber-800' :
                isWarning ? 'text-yellow-800' :
                'text-blue-800'
              }`}>
                {isExpired ? '⚠️ Sua licença expirou!' :
                 isUrgent ? '🔴 Sua licença expira em breve!' :
                 isWarning ? '⚠️ Atenção: Licença expirando' :
                 '📋 Lembrete de Renovação'}
              </h3>
              
              <p className={`mt-1 ${
                isExpired ? 'text-rose-700' :
                isUrgent ? 'text-amber-700' :
                isWarning ? 'text-yellow-700' :
                'text-blue-700'
              }`}>
                {isExpired ? (
                  <>Sua assinatura do plano <strong>{planName}</strong> expirou. Renove agora para continuar usando o sistema.</>
                ) : (
                  <>Seu plano <strong>{planName}</strong> expira em <strong>{daysRemaining} dia{daysRemaining !== 1 ? 's' : ''}</strong>. Renove para não perder acesso ao sistema.</>
                )}
              </p>
              
              <div className="mt-3 flex flex-wrap gap-3">
                <Button 
                  onClick={() => navigate('/subscription')}
                  className={`${
                    isExpired ? 'bg-rose-600 hover:bg-rose-700' :
                    isUrgent ? 'bg-amber-600 hover:bg-amber-700' :
                    isWarning ? 'bg-yellow-600 hover:bg-yellow-700' :
                    'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {isExpired ? 'Renovar Agora' : 'Gerenciar Assinatura'}
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => navigate('/plans')}
                  className={`${
                    isExpired ? 'border-rose-400 text-rose-700 hover:bg-rose-100' :
                    isUrgent ? 'border-amber-400 text-amber-700 hover:bg-amber-100' :
                    isWarning ? 'border-yellow-400 text-yellow-700 hover:bg-yellow-100' :
                    'border-blue-400 text-blue-700 hover:bg-blue-100'
                  }`}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Ver Planos
                </Button>
              </div>
            </div>
            
            {/* Badge de dias restantes */}
            <div className={`text-center px-4 py-2 rounded-lg flex-shrink-0 ${
              isExpired ? 'bg-rose-500' :
              isUrgent ? 'bg-amber-500' :
              isWarning ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}>
              <div className="text-white text-3xl font-bold">{isExpired ? '!' : daysRemaining}</div>
              <div className="text-white text-xs">{isExpired ? 'Expirado' : 'dias'}</div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Dashboard</h1>
        <p className="text-slate-600 text-lg">Bem-vindo, {user?.name}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Card key={index} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`dashboard-card-${index}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">{card.title}</CardTitle>
                <div className={`w-10 h-10 ${card.bgColor} rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${card.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{card.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
